import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Clock, X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SearchHistoryProps {
  onSelectQuery: (query: string, filters: any) => void;
}

export function SearchHistory({ onSelectQuery }: SearchHistoryProps) {
  const queryClient = useQueryClient();

  const { data: history = [] } = useQuery({
    queryKey: ["search-history"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from("search_history")
        .select("*")
        .eq("user_id", user.id)
        .order("searched_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("search_history")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["search-history"] });
    },
  });

  if (history.length === 0) return null;

  return (
    <div className="space-y-2 p-2">
      <p className="text-xs text-muted-foreground px-2">Recent searches</p>
      {history.map((item: any) => (
        <div
          key={item.id}
          className="flex items-center justify-between p-2 hover:bg-accent rounded-md group cursor-pointer"
          onClick={() => onSelectQuery(item.query, item.filters)}
        >
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Clock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <span className="text-sm truncate">{item.query}</span>
            {item.result_count > 0 && (
              <span className="text-xs text-muted-foreground">
                ({item.result_count})
              </span>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              deleteMutation.mutate(item.id);
            }}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      ))}
    </div>
  );
}
